import React, { useEffect, useState } from "react";
import Table from "../../components/ui/Table";
import StatusBadge from "../../components/ui/StatusBadge";
import { getColumnByType } from "../../helpers/tableHelper";
import { useOutletContext, useParams } from "react-router-dom";
import Spinner from "../../components/ui/Spinner";
import { toast } from "react-toastify";
import { convertAmountToPretty, prettyBce, prettyIdProcurement } from "../../helpers/formatHelper";
import WordIcon from "../../assets/icons/word";
import { DocumentType, getDocumentByType } from "../../helpers/documentHelper";
import Loader from "../../components/ui/Loader";
import Tabs from "../../components/ui/Tabs";
import OfferDisplay from "./components/OfferDisplay";
import { getAttributionByUuidProcurement, updateProcurement, uploadDocument } from "../../services/api/procurementService";
import PdfIcon from "../../assets/icons/pdf";
import { Step } from "../../helpers/stepHelper";

export default function Attribution() {
  const { procurement, handleUpdateProcurement } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const { uuidProcurement } = useParams();
  const [attribution, setAttribution] = useState(null);
  const [offerSelected, setOfferSelected] = useState(null);
  const [columns, setColumns] = useState(null);
  const [tab, setTab] = useState(0);
  const attributionDoc = getDocumentByType(procurement.documents, [DocumentType.ATTRIBUTION]);
  const notificationDoc = getDocumentByType(procurement.documents, [DocumentType.ATTRIBUTION_NOTIFICATION, DocumentType.ATTRIBUTION_NOTIFICATION_UNSIGNED]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function fetch() {
      try {
        setIsLoading(true);
        let result = await getAttributionByUuidProcurement(uuidProcurement);
        if (result) {
          let columnsTemp = extractColumnsFromOffers(result.offers);
          setColumns(columnsTemp);
          setAttribution(result);
          setOfferSelected(result.offers.find(offer => offer.status === "SELECTED"));
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetch();
  }, [uuidProcurement]);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data?.action === "submission-completed") {
        try {
          setIsGenerating(true);
          await updateProcurement(uuidProcurement, { isGenerating: true });
          await handleUpdateProcurement();
          toast.success(procurement.attributionEditLink ? "Modifications envoyées." : "Données envoyées.");
        } catch (error) {
          toast.error(error.message);
        } finally {
          setIsGenerating(false);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleUpdateProcurement, procurement.attributionEditLink, uuidProcurement]);

  function extractColumnsFromOffers(offers) {
    let columnsTemp = [{ key: "name", title: "Critère", type: getColumnByType.TEXT }];
    for (const offer of offers) {
      columnsTemp.push({
        key: offer.enterprise.name,
        title: offer.enterprise.name,
        type: getColumnByType.PERCENT,
      });
    }
    return columnsTemp;
  }

  function getIconForFilename(name = "") {
    const lower = name.toLowerCase();
    if (lower.endsWith(".pdf")) return <PdfIcon />;
    if (lower.endsWith(".doc") || lower.endsWith(".docx")) return <WordIcon />;
    return null;
  }

  async function uploadSelectedFile(selectedFile) {
    if (!selectedFile) {
      toast.error("Veuillez sélectionner un fichier.");
      return;
    }
    try {
      setIsSubmitting(true);
      await uploadDocument(uuidProcurement, selectedFile, DocumentType.ATTRIBUTION_NOTIFICATION);
      await handleUpdateProcurement();
      setIsModalOpen(false);
      setFile(null);
      toast.success("Le fichier a bien été envoyé !");
    } catch (err) {
      toast.error(err?.message || "Impossible d'uploader le fichier !");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handlePickFile(e) {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
  }

  function handleRemoveFile() {
    setFile(null);
    const input = document.getElementById("hidden-file-input");
    if (input) input.value = "";
  }

  if (isLoading || !procurement.id) {
    return <Spinner />;
  }

  if (isGenerating) {
    return <Loader text="Chargement des données en cours... Merci de votre patience 😊" />;
  }

  return (
    <>
      {
        !attribution
          ? (
            <div className="flex flex-col h-full">
              <div className="h-full self-center content-center">
                <StatusBadge text="<---- Encodez au moins une offre en cliquant sur un soumissionnaire pour pouvoir valider le rapport !" color="red" />
              </div>
            </div>
          )
          : (
            <div className="relative flex flex-col space-y-10">
              {procurement.step.name === Step.VALIDATION_ATTRIBUTION && procurement.isUserAction && (
                <div className="absolute right-0 -top-2">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-white shadow-md shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 active:translate-y-0"
                    aria-label="Uploader un document financier"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 transition-transform group-hover:-translate-y-0.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <path d="M7 10l5-5 5 5" />
                      <path d="M12 15V5" />
                    </svg>
                    <span className="font-medium">Uploader la notification d'attribution signée</span>
                  </button>
                </div>
              )}

              <div className="flex justify-between">
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold">Rapport d'attribution</h2>
                  {attributionDoc
                    ? (
                      <div
                        className="w-10 h-auto cursor-pointer text-blue-600 flex items-center gap-2"
                        onClick={() => window.open(attributionDoc.url, "_blank")}
                      >
                        {attributionDoc.name.endsWith(".pdf") ? <PdfIcon /> : <WordIcon />}
                      </div>
                    )
                    : isGenerating
                      ? (
                        <p className="text-gray-500">
                          Document en cours de génération{" "}
                          <span className="text-sm italic">
                            (veuillez rafraîchir la page d’ici quelques secondes).
                          </span>
                        </p>
                      )
                      : (
                        <span className="text-gray-500">
                          Aucun document{" "}
                          <span className="text-sm italic">(il sera généré après validation du formulaire)</span>
                        </span>
                      )}
                </div>
                {notificationDoc
                  && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold">Notification d'attribution</h2>
                      <div
                        className="w-10 h-auto cursor-pointer text-blue-600 flex items-center gap-2"
                        onClick={() => window.open(notificationDoc.url, "_blank")}
                      >
                        {notificationDoc.name.endsWith(".pdf") ? <PdfIcon /> : <WordIcon />}
                      </div>
                    </div>
                  )}
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-semibold">Évaluation de l'offre sous l'angle des critères d'attribution</h2>
                <Tabs tabs={attribution.offers.map(offer => offer.enterprise.name)} activeTab={tab} handleTab={setTab} />
                <OfferDisplay key={attribution.offers[tab].enterprise.uuid} offer={attribution.offers[tab]} criteria={procurement.criteria} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">Comparaison des points des offres</h2>
                <Table columns={columns} data={attribution.resume} showSum />
              </div>
              {offerSelected && (
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold">Proposition de décision d'attribution</h2>
                  <p>
                    Compte tenu de ce qui précède, il est suggéré d’attribuer le marché AdN {prettyIdProcurement(procurement.id)} au soumissionnaire sélectionné ayant déposé l’offre régulière économiquement la plus avantageuse, conformément aux dispositions de l’article 81 de la loi du 17 juin 2016, soit à {offerSelected.enterprise.name} de {offerSelected.enterprise.postalCode} - {offerSelected.enterprise.locality} ({prettyBce(offerSelected.enterprise.enterpriseNumber)}) pour un montant maximal de {convertAmountToPretty(offerSelected.amountVAT)} TVAC
                  </p>
                </div>
              )}
            </div>
          )
      }

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                aria-label="Fermer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <input
                id="hidden-file-input"
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={handlePickFile}
                disabled={isSubmitting}
              />

              {!file && (
                <label
                  htmlFor="hidden-file-input"
                  className={`block w-full cursor-pointer select-none rounded-xl px-5 py-3 text-center text-white shadow-lg transition focus:outline-none focus:ring-2 ${isSubmitting ? "bg-gray-400 cursor-not-allowed shadow-none" : "bg-blue-600 hover:bg-blue-700 active:scale-[0.99] focus:ring-blue-300"}`}
                >
                  Sélectionner la notification d'attribution signée
                </label>
              )}

              {file && (
                <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-6 w-6 text-blue-600">
                      {getIconForFilename(file.name) || (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16l4-4h10a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
                      <p className="text-xs text-gray-500">{Math.ceil((file.size || 0) / 1024)} Ko</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={isSubmitting}
                    className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 ${isSubmitting ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-200"}`}
                    aria-label="Supprimer le fichier sélectionné"
                  >
                    Supprimer
                  </button>
                </div>
              )}

              {file && (
                <button
                  type="button"
                  onClick={() => uploadSelectedFile(file)}
                  disabled={isSubmitting}
                  className={`w-full rounded-xl px-5 py-3 text-center font-medium text-white shadow-lg transition focus:outline-none focus:ring-2 ${isSubmitting ? "bg-gray-400 cursor-not-allowed shadow-none" : "bg-green-600 hover:bg-green-700 active:scale-[0.99] focus:ring-green-300"}`}
                  aria-label="Envoyer le fichier sélectionné"
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer ce fichier"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
